import React, { useState } from "react";
import {
  Button,
  Container,
  TextField,
  Typography,
} from "@material-ui/core";
import { Link as RouterLink, useHistory } from "react-router-dom";

import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";

interface SignupProps {
  cartCount?: number;
  onCartClick?: () => void;
}

const Signup: React.FC<SignupProps> = ({
  cartCount = 0,
  onCartClick = () => {},
}) => {
  const history = useHistory();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    setError("");

    // Basic validation
    if (!name.trim() || !email.trim() || !password || !confirmPassword) {
      setError("Please fill in all required fields.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);

      const payload = JSON.stringify({
        email: email.trim(),
        password,
        business: "69f76a635d99871fd9349c1e",
        name: name.trim(),
        role: "user",
      });

      const response = await fetch(
        "https://shopify.anainfo.ai/api/users",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjY5N2ViNzY5YzJhMzBlNWZjNTY1NTg0YSIsInJvbGUiOiJzdXBlcmFkbWluIiwiaWF0IjoxNzg3NzE2NTU2LCJleHAiOjE3ODc4MDI5NTZ9.kjlQsUrQIn3jlksdO4rHYxlMyMl5la03oVDDbiMzrOU`,
          },
          body: payload,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();

        let errorMessage = "Signup failed. Please try again.";

        try {
          const errorData = JSON.parse(errorText);

          errorMessage =
            errorData?.message ||
            errorData?.error ||
            errorData?.errors ||
            errorMessage;
        } catch {
          if (errorText) {
            errorMessage = errorText;
          }
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();

      console.log("Signup response:", data);

      alert("Signup successful!");

      history.push("/login");
    } catch (err: any) {
      console.error("Signup error:", err);

      setError(
        err?.message ||
          "Unable to create your account. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <Header
        cartCount={cartCount}
        onCartClick={onCartClick}
      />

      <section className="auth-section">
        <Container maxWidth="lg">
          <div className="auth-wrapper">

            {/* =========================
                GREEN SIDE CARD
            ========================= */}

            <div className="auth-side-card signup-side-card">

              <Typography className="auth-side-eyebrow">
                Join Our Community
              </Typography>

              <Typography className="auth-side-title">
                Grow With
                <br />
                Organic Shop
              </Typography>

              <div className="auth-side-divider" />

              <Typography className="auth-side-text">
                Create your account and enjoy a
                simple, fresh and natural shopping
                experience.
              </Typography>

              <Typography className="auth-benefit">
                ✓ Fresh Organic Products
              </Typography>

              <Typography className="auth-benefit">
                ✓ Exclusive Offers
              </Typography>

              <Typography className="auth-benefit">
                ✓ Easy Shopping Experience
              </Typography>

              <Button
                component={RouterLink}
                to="/login"
                className="auth-side-button"
                disabled={loading}
              >
                BACK TO LOGIN
              </Button>

            </div>

            {/* =========================
                SIGNUP FORM
            ========================= */}

            <div className="auth-form-container signup-form">

              <Typography className="auth-eyebrow">
                Create Account
              </Typography>

              <Typography className="auth-title">
                Sign Up
              </Typography>

              <Typography className="auth-description">
                Create your Organic Shop account
                and start shopping fresh.
              </Typography>

              <form
                className="auth-form"
                onSubmit={handleSubmit}
              >

                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Enter your name"
                  value={name}
                  onChange={(event) =>
                    setName(event.target.value)
                  }
                  className="auth-input"
                  disabled={loading}
                  required
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Enter your email"
                  type="email"
                  value={email}
                  onChange={(event) =>
                    setEmail(event.target.value)
                  }
                  className="auth-input"
                  disabled={loading}
                  required
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Create a password"
                  type="password"
                  value={password}
                  onChange={(event) =>
                    setPassword(event.target.value)
                  }
                  className="auth-input"
                  disabled={loading}
                  required
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Confirm your password"
                  type="password"
                  value={confirmPassword}
                  onChange={(event) =>
                    setConfirmPassword(event.target.value)
                  }
                  className="auth-input"
                  disabled={loading}
                  required
                />

                {/* API / VALIDATION ERROR */}

                {error && (
                  <Typography
                    color="error"
                    variant="body2"
                    style={{
                      marginTop: 8,
                      marginBottom: 8,
                    }}
                  >
                    {error}
                  </Typography>
                )}

                <label className="terms-checkbox">
                  <input
                    type="checkbox"
                    required
                    disabled={loading}
                  />

                  <span>
                    I agree to the Terms & Conditions
                  </span>
                </label>

                <Button
                  type="submit"
                  fullWidth
                  className="auth-submit-button"
                  disabled={loading}
                >
                  {loading
                    ? "CREATING ACCOUNT..."
                    : "CREATE ACCOUNT"}
                </Button>

              </form>

              <div className="auth-switch">

                <Typography>
                  Already have an account?
                </Typography>

                <Button
                  component={RouterLink}
                  to="/login"
                  className="auth-link-button"
                  disabled={loading}
                >
                  Login
                </Button>

              </div>

            </div>
          </div>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default Signup;